import {
  Box,
  FormControl,
  Input,
  InputAdornment,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import {
  numberToString,
  stringToNumber,
} from '../../../special/numberConverter';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { tokens } from '../../../../global/theme/tokens';

const EntryEditableWithUnit = ({
  col,
  item,
  onChange,
  onFocus = () => {},
  onUnitChange,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const textFieldRef = useRef(null);
  const unitFieldRef = useRef(null);
  const hasInteracted = useRef(false);
  const { t } = useTranslation();
  const [value, setValue] = useState(null);
  const [shownValue, setShownValue] = useState(null);
  const [unitValue, setUnitValue] = useState(item.unit || '');
  const [isFocused, setIsFocused] = useState(false);
  const [debouncedValue] = useDebounce(value, 400);
  const [debouncedUnit] = useDebounce(unitValue, 400);

  const numToStringDecimalPlaces = 16;

  const usePercent =
    (item.type == 'hoaiPhase' || item.type == 'percent') && col.key == 'amount';

  useEffect(() => {
    if (debouncedValue && hasInteracted.current) onChange({ target: { value: debouncedValue } });
  }, [debouncedValue]);

  useEffect(() => {
    if (onUnitChange && debouncedUnit !== item.unit) {
      onUnitChange({ target: { value: debouncedUnit } });
    }
  }, [debouncedUnit]);

  useEffect(() => {
    if (!isFocused || item[col.key] !== stringToNumber(value)) {
      if (usePercent) {
        setShownValue(numberToString(item[col.key] * 100, col.decimalPlaces ?? 2));
        setValue(numberToString(item[col.key], numToStringDecimalPlaces));
      } else
        setValue(
          col.number
            ? numberToString(item[col.key], col.decimalPlaces ?? 2)
            : item[col.key]
        );
    }
  }, [item[col.key]]);

  useEffect(() => {
    if (item.unit !== unitValue) {
      setUnitValue(item.unit || '');
    }
  }, [item.unit]);

  function handleFocus() {
    if (
      usePercent &&
      stringToNumber(value, numToStringDecimalPlaces) * 100 == 0
    ) {
      setValue('');
      setShownValue('');
    } else if (parseFloat(value) == 0) {
      setValue('');
    }
    onFocus(textFieldRef);
    setIsFocused(true);
  }

  function handleBlur(e) {
    if (usePercent) {
      setValue(
        numberToString(
          stringToNumber(e.target.value, numToStringDecimalPlaces) / 100,
          numToStringDecimalPlaces
        )
      );
      setShownValue(
        numberToString(
          stringToNumber(e.target.value, numToStringDecimalPlaces),
          col.decimalPlaces
        )
      );
    } else {
      const parsed = stringToNumber(e.target.value, numToStringDecimalPlaces);
      setValue(
        numberToString(
          parsed,
          col.decimalPlaces ?? 2
        )
      );
    }
    setIsFocused(false);
  }

  function handleChange(e) {
    hasInteracted.current = true;
    if (usePercent) {
      setValue(
        numberToString(
          stringToNumber(e.target.value, numToStringDecimalPlaces) / 100
        ),
        numToStringDecimalPlaces
      );
      setShownValue(e.target.value);
    } else setValue(e.target.value);
  }

  function handleUnitChange(e) {
    setUnitValue(e.target.value);
  }

  return (
    <Box
      className="flex items-center gap-1"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <FormControl sx={{ m: 0, flex: 1 }}>
        <Input
          disableInjectingGlobalStyles={true}
          ref={textFieldRef}
          onFocus={handleFocus}
          onBlur={col.number ? handleBlur : () => {}}
          size="small"
          className={col.className}
          id={'pos-input-' + col.key}
          value={(usePercent ? shownValue : value ?? '') ?? ''}
          onChange={handleChange}
          endAdornment={
            usePercent ? (
              <InputAdornment position="end">%</InputAdornment>
            ) : null
          }
        />
      </FormControl>
      {!usePercent && (
        <FormControl sx={{ m: 0, width: '50px' }}>
          <Input
            disableInjectingGlobalStyles={true}
            ref={unitFieldRef}
            size="small"
            placeholder={t('Eht.')}
            value={unitValue}
            onChange={handleUnitChange}
            sx={{
              fontSize: '0.75rem',
              '& input': {
                textAlign: 'center',
                padding: '3px 4px',
              },
            }}
          />
        </FormControl>
      )}
    </Box>
  );
};

export default EntryEditableWithUnit;
